import React from 'react';
import { connect } from 'react-redux';

import {
  workflowAddFinalizeTask,
  workflowRemoveFinalizeTask,
  workflowSetFinalizeCoordinator,
  workflowSetFinalizeTaskName,
  workflowSetStageConditionRule,
  workflowSortFinalizeTasks,
  workflowToggleSkipStage,
} from '~/actions/workflow';
import { FormField } from '~/eds/src/components/FormField/FormField';
import { TicketStageType } from '~/enums/TicketStageType';
import { useIntl } from 'react-intl';
import { getFinalizeErrors, getFinalizeTasks } from '~/reducers/workflow';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import { coerceName } from '~/utils/workflow';

import SkipStageAccordion from '../../shared/SkipStageAccordion';
import StageItems from '../../shared/StageItems';
import WorkflowUserRoleSingleSelect from '../../shared/WorkflowUserRoleSingleSelect';
import Phase from './Phase';

function Finalize({
  // connected
  conditionRule,
  coordinator,
  disabled,
  errors,
  isSkipped,
  finalizePhases,
  workflowAddFinalizeTask,
  workflowSetFinalizeCoordinator,
  workflowSortFinalizeTasks,
  workflowRemoveFinalizeTask,
  workflowSetFinalizeTaskName,
  workflowSetStageConditionRule,
  workflowToggleSkipStage,
}) {
  const intl = useIntl();

  const finalizeItems = finalizePhases.map((phase, index) => ({
    ...phase,
    title: coerceName(
      phase.title,
      intl.formatMessage({
        id: 'Finalize.defaultPhaseName',
        defaultMessage: 'Finalize Phase',
        description: 'Default name for a new finalize phase.',
      }),
      index,
    ),
    content: <Phase key={phase.id} phaseId={phase.id} position={index} />,
  }));

  const handleUpdateStageConditionRule = (updatedConditionRule) => {
    workflowSetStageConditionRule({
      conditionRule: updatedConditionRule,
      stage: TicketStageType.Finalize,
    });
  };

  const handleToggleSkipStage = (updatedIsSkipped) => {
    workflowToggleSkipStage({
      isSkipped: updatedIsSkipped,
      stage: TicketStageType.Finalize,
    });
  };

  return (
    <FlexLayout disabled={disabled} flexDirection="column" space={6}>
      <SkipStageAccordion
        conditionRule={conditionRule}
        isSkipped={isSkipped}
        onChange={handleUpdateStageConditionRule}
        onToggle={handleToggleSkipStage}
      />
      <FlexLayout flexDirection="column" mb={8} space={4}>
        <FormField
          description={intl.formatMessage({
            id: 'Finalize.coordinatorDescription',
            defaultMessage:
              'A task list coordinator will be in charge of making sure that all the tasks have been reviewed.',
            description: 'Description for task list coordinator field',
          })}
          id="workflow--finalize-coordinator"
          input={WorkflowUserRoleSingleSelect}
          label={intl.formatMessage({
            id: 'Finalize.taskListCoordinatorLabel',
            defaultMessage: 'Task List Coordinator',
            description: 'Label for task list coordinator field',
          })}
          placeholder={intl.formatMessage({
            id: 'Finalize.notSpecifiedPlaceholder',
            defaultMessage: 'Not specified',
            description: 'Placeholder for coordinator field when not specified',
          })}
          width="fullWidth"
          value={coordinator}
          onChange={workflowSetFinalizeCoordinator}
          error={
            errors.unspecifiedCoordinator
              ? intl.formatMessage({
                  id: 'Finalize.specifyCoordinatorError',
                  defaultMessage: 'You need to specify a finalize coordinator.',
                  description:
                    'Error message when coordinator is not specified',
                })
              : undefined
          }
        />
      </FlexLayout>
      <StageItems
        disabled={disabled}
        items={finalizeItems}
        invalidPhaseIds={errors.invalidPhaseIds}
        removeModalTitle={intl.formatMessage({
          id: 'Finalize.removeFinalizePhaseTitle',
          defaultMessage: 'Remove Finalize Phase',
          description: 'Modal title for removing finalize phase',
        })}
        sortModalTitle={intl.formatMessage({
          id: 'Finalize.reorderFinalizePhasesTitle',
          defaultMessage: 'Reorder Finalize Phases',
          description: 'Modal title for reordering finalize phases',
        })}
        onRemove={workflowRemoveFinalizeTask}
        onSort={workflowSortFinalizeTasks}
        onEditTitle={workflowSetFinalizeTaskName}
        newItemAction={{
          id: 'workflow--add-finalize-phase',
          text: intl.formatMessage({
            id: 'Finalize.addFinalizePhaseText',
            defaultMessage: 'Add finalize phase',
            description: 'Button text for adding new finalize phase',
          }),
          handler: workflowAddFinalizeTask,
        }}
      />
    </FlexLayout>
  );
}

function mapStateToProps({ builder, workflow }) {
  const stage = workflow.stages[TicketStageType.Finalize];
  return {
    conditionRule: stage.conditionRule,
    coordinator: stage.coordinators[0] || null,
    isSkipped: !stage.isEnabled,
    disabled: !builder.shouldEnableAllComponents,
    finalizePhases: getFinalizeTasks(workflow),
    errors: getFinalizeErrors(workflow),
  };
}

export default connect(mapStateToProps, {
  workflowAddFinalizeTask,
  workflowSetFinalizeCoordinator,
  workflowSortFinalizeTasks,
  workflowRemoveFinalizeTask,
  workflowSetFinalizeTaskName,
  workflowSetStageConditionRule,
  workflowToggleSkipStage,
})(Finalize);
